import { useUnit } from 'effector-react';
import _ from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import { $queryParams } from 'models/location';
import { $global } from 'models/store-global';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useLinkingVerification } from 'components/ProfileLinker/ProfileLinker';
import ChangeRoleWarningPage from 'components/pages/ChangeRoleWarningPage/ChangeRoleWarningPage';
import { CommunityDashboard, EPDashboard } from 'components/pages/Dashboard/DashboardPage';
import LoadingPage from 'components/pages/LoadingPage/LoadingPage';

import { lazyWithRetry } from 'utils/lazy-with-retry';

import useTokenRefresher from 'hooks/useTokenRefresher';

const AdvisorApp = lazyWithRetry(() => import('./AdvisorApp'));
const AdminApp = lazyWithRetry(() => import('./AdminApp'));
const TermsAndConditionsPage = lazyWithRetry(() => import('components/TermsAndConditionsPage/TermsAndConditionsPage'));
const ValidationPage = lazyWithRetry(() => import('components/pages/ValidationPage/ValidationPage'));
const AccountPage = lazyWithRetry(() => import('components/Account/AccountPage'));
const QuestionnaireForm = lazyWithRetry(() => import('components/QuestionnaireForm/QuestionnaireForm'));
const AfterPaymentPage = lazyWithRetry(() => import('components/pages/AfterPayment/AfterPaymentPage'));
const AfterGiftTrustPaymentPage = lazyWithRetry(
    () => import('components/pages/AfterGiftTrustPayment/AfterGiftTrustPaymentPage'),
);
const AfterShippingPaymentPage = lazyWithRetry(
    () => import('components/pages/AfterShippingPayment/AfterShippingPaymentPage'),
);
const ChangeEmailConfirmation = lazyWithRetry(
    () => import('components/pages/ChangeEmailConfirmation/ChangeEmailConfirmation'),
);
const DocsGenerationWaiting = lazyWithRetry(
    () => import('components/pages/DocsGenerationWaiting/DocsGenerationWaiting'),
);
const LinkingVerificationsPage = lazyWithRetry(
    () => import('components/pages/LinkingVerificationsPage/LinkingVerificationsPage'),
);
const NotificationsPage = lazyWithRetry(() => import('components/pages/Notifications/NotificationsPage'));
const ParticipantsInfoPage = lazyWithRetry(() => import('components/pages/ParticipantsInfo/ParticipantsInfoPage'));
const PaymentPage = lazyWithRetry(() => import('components/pages/PaymentPage/PaymentPage'));
const PrePaymentPage = lazyWithRetry(() => import('components/pages/PrePayment/PrePaymentPage'));
const GiftTrustPrePaymentPage = lazyWithRetry(
    () => import('components/pages/GiftTrustPrePayment/GiftTrustPrePaymentPage'),
);
const Summary = lazyWithRetry(() => import('components/pages/Summary/Summary'));
const LogoutPage = lazyWithRetry(() => import('components/pages/LogoutPage/LogoutPage'));

const UsersRoutes = () => {
    const isRelevantSigned = useUnit($global.map((state) => state?.tac?.user?.isRelevantSigned || false));

    const isSigningInProgress = useUnit($$api.termsAndConditions.signRelevantFx.pending);

    if (!isRelevantSigned && !isSigningInProgress) {
        return (
            <Routes>
                <Route path="/tac" element={<TermsAndConditionsPage role="user" />} />
                <Route path="/*" element={<Navigate to="/tac" />} />
            </Routes>
        );
    }

    return (
        <Routes>
            <Route path="/validate" element={<ValidationPage />} />
            <Route path="/validate/:guid" element={<ValidationPage />} />
            <Route path="/document/:questionnaireType/:stateCode/:qInstanceId" element={<QuestionnaireForm />} />
            <Route
                path="/document/:questionnaireType/:stateCode/:qInstanceId/:section"
                element={<QuestionnaireForm />}
            />
            <Route path="/pre-payment" element={<PrePaymentPage />} />
            <Route path="/pre-payment-gt" element={<GiftTrustPrePaymentPage />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/participants-info" element={<ParticipantsInfoPage />} />
            <Route path="/summary" element={<Summary />} />
            <Route path="/docs-generation" element={<DocsGenerationWaiting />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/profile" element={<AccountPage />} />
            <Route path="/change_email_confirmation" element={<ChangeEmailConfirmation />} />
            <Route path="/dashboard/*" element={<EPDashboard />} />
            <Route path="/community/*" element={<CommunityDashboard />} />
            <Route path="/after-payment/*" element={<AfterPaymentPage />} />
            <Route path="/after-payment-gt/*" element={<AfterGiftTrustPaymentPage />} />
            <Route path="/after-shipping-payment/*" element={<AfterShippingPaymentPage />} />
            <Route path="/*" element={<Navigate to="/dashboard" />} />
        </Routes>
    );
};

const $systemRoles = $$auth.$currentParticipant.map((p) => p?.systemRoles || ['user' as const]);

const UserApp = () => {
    const { pathname } = useLocation();
    const { role } = useUnit($queryParams);

    const initialized = useUnit($global.map((state) => state.auth?.initialized || false));
    const isRelevantSigned = useUnit($global.map((state) => state?.tac?.user?.isRelevantSigned || false));
    const isRelevantSignedLoaded = _.isBoolean(isRelevantSigned);
    const isSignUpInProgress = useUnit($global.map((state) => state?.signUpInProgress || false));

    const isAdmin = useUnit($systemRoles.map((roles) => roles.includes('admin')));
    const isAdvisor = useUnit($systemRoles.map((roles) => roles.includes('advisor')));
    const isOriginator = useUnit($systemRoles.map((roles) => roles.includes('user')));

    useTokenRefresher();
    useLinkingVerification();

    if (!isRelevantSignedLoaded || !initialized || isSignUpInProgress) {
        return <LoadingPage />;
    }

    if (pathname === '/signup' && role === 'advisor') {
        return <Navigate to="/advisor" />;
    }

    return (
        <Routes>
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/change-role" element={<ChangeRoleWarningPage />} />
            <Route path="/advisor/change-role" element={<ChangeRoleWarningPage />} />
            <Route path="/link/:code" element={<LinkingVerificationsPage />} />
            <Route
                path="/advisor/*"
                element={isAdvisor || isAdmin ? <AdvisorApp /> : <Navigate to="/advisor/change-role" />}
            />
            <Route
                path="/admin"
                element={isAdmin ? <AdminApp /> : <Navigate to={isAdvisor ? '/advisor/dashboard' : '/dashboard'} />}
            />
            <Route
                path="/debug/:key"
                element={isAdmin ? <AdminApp /> : <Navigate to={isAdvisor ? '/advisor/dashboard' : '/dashboard'} />}
            />
            <Route
                path="/"
                element={isOriginator || isAdmin ? <UsersRoutes /> : <Navigate to="/advisor/dashboard" />}
            />
            <Route path="/*" element={isOriginator || isAdmin ? <UsersRoutes /> : <Navigate to="/change-role" />} />
        </Routes>
    );
};

export default UserApp;
